// Pure helpers for the Owner Statistics chart: series -> chart dataset,
// bucket labels, empty detection. No React and no x-charts import, so the
// unit test stays free of the chart library.
import {
  IOwnerStatisticsBucket,
  OwnerStatisticsGranularity,
  OwnerStatisticsRange,
} from "../../../../services/api.types";

export type OwnerStatsMetric = "total" | "completed" | "cancelled" | "missed";

export const OWNER_STATS_RANGES: OwnerStatisticsRange[] = ["7d", "30d", "90d", "12m"];

// MUST be a `type` alias, not an `interface`: x-charts' `dataset` prop needs an
// index-signature-compatible element, and interfaces are not implicitly
// assignable to index signatures.
export type OwnerStatsChartRow = {
  label: string;
  total: number;
  completed: number;
  cancelled: number;
  missedExpired: number;
  missedDeclined: number;
};

// Locale-free on purpose (deterministic in tests, no Intl).
// day "2026-10-05" -> "05.10"; month "2026-10" -> "10.2026".
export function formatBucketLabel(
  date: string,
  granularity: OwnerStatisticsGranularity
): string {
  const [yyyy, mm, dd] = date.split("-");
  return granularity === "month" ? `${mm}.${yyyy}` : `${dd}.${mm}`;
}

export function buildChartDataset(
  series: IOwnerStatisticsBucket[],
  granularity: OwnerStatisticsGranularity
): OwnerStatsChartRow[] {
  return series.map((b) => ({
    label: formatBucketLabel(b.date, granularity),
    total: b.total,
    completed: b.completed,
    cancelled: b.cancelled,
    missedExpired: b.missedExpired,
    missedDeclined: b.missedDeclined,
  }));
}

// True when every bucket is 0 for the metric (or the series is empty).
// "missed" sums expired + declined.
export function isMetricEmpty(
  series: IOwnerStatisticsBucket[],
  metric: OwnerStatsMetric
): boolean {
  return series.every((b) =>
    metric === "missed"
      ? b.missedExpired + b.missedDeclined === 0
      : b[metric] === 0
  );
}
